// frontend/src/components/Navigation.js

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  Box,
  Typography,
  Divider,
  Chip
} from '@mui/material';
import {
  Dashboard,
  People,
  Settings,
  Assessment,
  Security,
  Business,
  AccountCircle,
  AdminPanelSettings,
  ViewList,
  BarChart,
  Factory,
  Build,
  ShoppingCart,
  CloudUpload,
  Inventory,
  Category,
  AccountTree,
  SyncAlt,
  Upload,
  SwapHoriz,
  LocalShipping,
  CompareArrows,
  Add,
  EventNote,
  Backup,
  History,
  VpnKey,
  FactCheck,
  ReportProblem,
  DeleteSweep
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const Navigation = ({ open, onClose }) => {
  const { user, hasPageAccess } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isAdmin = user?.is_staff || user?.role === 'admin';

  // Define menu sections with items
  const menuSections = [
    {
      title: null, // No title for first section
      items: [
        { 
          text: 'Admin Dashboard', 
          icon: <AdminPanelSettings />, 
          path: '/admin-dashboard', 
          key: 'admin-dashboard', 
          adminOnly: true 
        },
        { 
          text: 'Dashboard', 
          icon: <Dashboard />, 
          path: '/dashboard', 
          key: 'dashboard',
          alwaysShow: true  
        },
        { 
          text: 'My Profile', 
          icon: <AccountCircle />, 
          path: '/profile', 
          key: 'profile',
          alwaysShow: true  
        },
      ]
    },
    {
      title: 'Sage Integration',
      items: [
        { 
          text: 'Sage Import', 
          icon: <CloudUpload />, 
          path: '/manufacturing/sage-import', 
          key: 'sage-import' 
        },
        { 
          text: 'Code Mappings', 
          icon: <SyncAlt />,  
          path: '/manufacturing/sage-mappings', 
          key: 'sage-mappings' 
        },
      ]
    },
    {
      title: 'Orders & Customers',
      items: [
        { 
          text: 'Customer Orders', 
          icon: <ShoppingCart />, 
          path: '/manufacturing/customer-orders', 
          key: 'customer-orders' 
        },
        { 
          text: 'Customers', 
          icon: <Business />, 
          path: '/manufacturing/customers', 
          key: 'customers' 
        },
      ]
    },
    {
      title: 'Dispatch',
      items: [
        { 
          text: 'Dispatch Management', 
          icon: <LocalShipping />, 
          path: '/dispatch', 
          key: 'dispatch' 
        },
      ]
    },
    {
      title: 'Manufacturing',
      items: [
        { 
          text: 'Manufacturing', 
          icon: <Factory />, 
          path: '/manufacturing', 
          key: 'manufacturing' 
        },
        { 
          text: 'Work Orders', 
          icon: <ViewList />, 
          path: '/manufacturing/work-orders', 
          key: 'work-orders' 
        },
        { 
          text: 'Create Work Order', 
          icon: <Add />, 
          path: '/manufacturing/create-work-order', 
          key: 'create-work-order' 
        },
        { 
          text: 'Recalculate Orders',
          icon: <SyncAlt />,
          path: '/manufacturing/recalculate',
          key: 'recalculate'
        },                               
        { 
          text: 'Shop Floor', 
          icon: <Build />, 
          path: '/shop-floor/all', 
          key: 'shop-floor' 
        },
        { 
          text: 'Inventory', 
          icon: <Inventory />, 
          path: '/manufacturing/inventory', 
          key: 'inventory' 
        },
        { 
          text: 'Stock Allocations', 
          icon: <CompareArrows />, 
          path: '/manufacturing/allocations', 
          key: 'allocations' 
        },
        {
          text: 'Stock Conversion',
          icon: <SwapHoriz />,
          path: '/manufacturing/convert',
          key: 'convert'
        },
      ]
    },
    {
      title: 'Manufacturing V2 (Beta)',
      items: [
        {
          text: 'Dashboard',
          icon: <Dashboard />,
          path: '/manufacturing/v2/dashboard',
          key: 'manufacturing-dashboard-v2'
        },
        {
          text: 'KPIs',
          icon: <Assessment />,
          path: '/manufacturing/v2/kpis',
          key: 'kpi-dashboard-v2'
        },
        {
          text: 'Work Orders V2',
          icon: <ViewList />,
          path: '/manufacturing/v2/work-orders',
          key: 'work-orders-v2'
        },
        {
          text: 'Create Work Order V2',
          icon: <Add />,
          path: '/manufacturing/v2/work-orders/new',
          key: 'work-orders-v2-create'
        },
        {
          text: 'Shop Floor V2',
          icon: <Build />,
          // Land on Final Assembly by default — the all-operations view is a
          // click away via the terminal's "All" button.
          path: '/manufacturing/v2/shop-floor/OP-25',
          key: 'shop-floor-v2'
        },
        {
          text: 'Transaction History',
          icon: <History />,
          path: '/manufacturing/v2/history',
          key: 'transaction-history-v2'
        },
        {
          text: 'Capacity Planning',
          icon: <EventNote />,
          path: '/manufacturing/v2/capacity',
          key: 'capacity-planning-v2'
        },
        {
          text: 'WO Creation Log',
          icon: <FactCheck />,
          path: '/manufacturing/v2/wo-creation-log',
          key: 'wo-creation-log'
        },
        {
          text: 'Material Shortages',
          icon: <ReportProblem />,
          path: '/manufacturing/v2/shortages',
          key: 'material-shortages-v2'
        },
        {
          text: 'Scrap',
          icon: <DeleteSweep />,
          path: '/manufacturing/v2/scrap',
          key: 'scrap-v2'
        },
      ]
    },
    {
      title: 'Product Data',
      items: [
        { 
          text: 'Items', 
          icon: <Category />, 
          path: '/manufacturing/items', 
          key: 'items' 
        },
        { 
          text: 'BOM', 
          icon: <AccountTree />, 
          path: '/manufacturing/bom', 
          key: 'bom' 
        },
        { 
          text: 'BOM Configurator',
          icon: <AccountTree />, 
          path: '/manufacturing/bom-configurator', 
          key: 'bom-configurator' 
        },
        { 
          text: 'BOM Swap', 
          icon: <SwapHoriz />, 
          path: '/manufacturing/bom-swap', 
          key: 'bom-swap' 
        },
        { 
          text: 'Operations', 
          icon: <Build />, 
          path: '/manufacturing/operations', 
          key: 'operations' 
        },
      ]
    },
    {
      title: 'Planning',
      items: [
        {
          text: 'Capacity Planning',
          icon: <EventNote />,
          path: '/capacity-planning',
          key: 'capacity-planning'
        },
        {
          text: 'Stock Procurement',
          icon: <ShoppingCart />,
          path: '/stock-procurement',
          key: 'stock-procurement'
        },
      ]
    },
    {
      title: 'Reports & Analytics',
      items: [
        { 
          text: 'Analytics', 
          icon: <Assessment />, 
          path: '/analytics', 
          key: 'analytics' 
        },
          { 
          text: 'Data Dashboard', 
          icon: <BarChart />,
          path: '/tv-dashboard', 
          key: 'tv-dashboard' 
        },
        { 
          text: 'Reports', 
          icon: <BarChart />, 
          path: '/reports', 
          key: 'reports' 
        },
        { 
          text: 'Tasks', 
          icon: <ViewList />, 
          path: '/tasks', 
          key: 'tasks' 
        },
      ]
    },
    {
      title: 'Administration',
      items: [
        {
          text: 'User Management',
          icon: <People />,
          path: '/users',
          key: 'users',
          adminOnly: true
        },
        {
          text: 'Orders API',
          icon: <VpnKey />,
          path: '/orders-api',
          key: 'orders-api',
          adminOnly: true
        },
        { 
          text: 'System Settings', 
          icon: <Settings />, 
          path: '/settings', 
          key: 'settings' 
        },
        {
          text: 'Audit Logs',
          icon: <Security />,
          path: '/audit',
          key: 'audit',
          adminOnly: true
        },
        {
          text: 'Backups',
          icon: <Backup />,
          path: '/admin/backups',
          key: 'backups',
          adminOnly: true
        },
      ]
    },
    {
      title: 'Data Management',
      items: [
        {
          text: 'Import Data',
          icon: <Upload />,
          path: '/import',
          key: 'import'
        },
      ]
    },
  ];

  // Filter items based on permissions
  const filterItems = (items) => {
    return items.filter(item => {
      if (item.adminOnly && !isAdmin) {
        return false;
      }
      if (item.alwaysShow) {
        return true;
      }
      return hasPageAccess(item.key);
    });
  };

  // Filter sections - only show sections that have visible items
  const visibleSections = menuSections
    .map(section => ({
      ...section,
      items: filterItems(section.items)
    }))
    .filter(section => section.items.length > 0);

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 280,
          background: 'linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)',
          borderRight: '1px solid rgba(0,0,0,0.08)'
        }
      }}
    >
      <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Welcome, {user?.username}!
        </Typography>
        <Chip
          label={isAdmin ? 'Administrator' : user?.role || 'User'}
          color={isAdmin ? 'primary' : 'secondary'}
          size="small"
          icon={isAdmin ? <AdminPanelSettings /> : <AccountCircle />}
        />
        {user?.department && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Department: {user.department}
          </Typography>
        )}
      </Box>

      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {visibleSections.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No pages available. Contact your administrator.
            </Typography>
          </Box>
        ) : (
          visibleSections.map((section, sectionIndex) => (
            <Box key={sectionIndex}>
              {section.title && (
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    px: 3, 
                    pt: 2, 
                    pb: 1, 
                    display: 'block',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }}
                >
                  {section.title}
                </Typography>
              )}
              <List disablePadding>
                {section.items.map((item) => (
                  <ListItem key={item.text} disablePadding sx={{ mb: 0.5, px: 2 }}>
                    <ListItemButton
                      onClick={() => handleNavigation(item.path)}
                      selected={location.pathname === item.path}
                      sx={{
                        borderRadius: 2,
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          '& .MuiListItemIcon-root': {
                            color: 'white'
                          },
                          '&:hover': {
                            backgroundColor: 'primary.dark'
                          }
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              {sectionIndex < visibleSections.length - 1 && (
                <Divider sx={{ my: 1, mx: 2 }} />
              )}
            </Box>
          ))
        )}
      </Box>

      {isAdmin && (
        <>
          <Divider sx={{ mx: 2 }} />
          <Box sx={{ px: 3, py: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Quick Actions
            </Typography>
            <ListItemButton
              onClick={() => window.open('/admin/', '_blank')}
              sx={{ borderRadius: 2, mb: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Business />
              </ListItemIcon>
              <ListItemText primary="Django Admin" />
            </ListItemButton>
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default Navigation;